import React, { ReactNode, useMemo } from 'react';
import {
  MenuItem,
  SxProps,
  InputAdornment,
  Stack,
  Divider,
  Typography,
} from '@mui/material';
import { UnitOfTime } from 'types';
import { useTranslation } from 'react-i18next';
import { capitalize, capitalizeFirstLetter } from 'utils';
import { TextValidator } from 'react-material-ui-form-validator';

export interface TimeUnitSelectProps {
  sx?: SxProps;
  variant?: 'standard' | 'outlined' | 'filled';
  margin?: 'none' | 'dense' | 'normal';
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  helperText?: string;
  required?: boolean;
  readonly?: boolean;
  label?: string;
  notched?: boolean;
  allowForever?: boolean;
  rateMode?: boolean; //  n per unit of time instead of n units of time
  endAdornment?: ReactNode;
  unitOfTime: UnitOfTime;
  setUnitOfTime: (value: UnitOfTime) => void;
  amountOfTime: number | string;
  setAmountOfTime: (value: number | string) => void;
}

const TimeUnitSelect = ({
  unitOfTime,
  setUnitOfTime,
  amountOfTime,
  setAmountOfTime,
  sx,
  required = false,
  variant = 'outlined',
  margin = 'none',
  fullWidth = true,
  rateMode = false,
  disabled,
  readonly,
  label,
  notched = true,
  allowForever = true,
  helperText,
  endAdornment,
}: TimeUnitSelectProps): ReactNode => {
  const { t } = useTranslation();

  const handleUnitOfTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnitOfTime(e.target.value as UnitOfTime);
  };

  const handleAmountOfTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow spaces and numbers only. Max value is 999.
    const formatted = e.target.value.replace(/[^0-9\s]/g, '');
    if (formatted.length < 4) {
      // Handle empty or whitespace-only input
      const trimmed = formatted.trim();
      if (trimmed === '') {
        setAmountOfTime('');
      } else {
        setAmountOfTime(parseInt(trimmed));
      }
    }
  };

  const amountOfTimeValue = useMemo(() => {
    if (unitOfTime === UnitOfTime.Forever) {
      return '1';
    }
    if (amountOfTime === 0) {
      return '0';
    }
    if (!amountOfTime) {
      return '';
    }
    return amountOfTime;
  }, [unitOfTime, amountOfTime]);

  const plural = useMemo(() => {
    return Number(amountOfTime) > 1;
  }, [amountOfTime]);

  const onBlur = () => {
    if (amountOfTime === '') {
      setAmountOfTime(0);
    }
  };

  return (
    <TextValidator
      sx={sx}
      margin={margin}
      variant={variant}
      fullWidth={fullWidth}
      id='time-unit-select'
      label={`${label}${required ? ' *' : ''}`}
      name={'unitOfTime-select'}
      value={unitOfTime}
      onChange={handleUnitOfTimeChange}
      disabled={disabled}
      helperText={capitalizeFirstLetter(helperText)}
      validators={required ? ['required'] : []}
      errorMessages={required ? [t('common.required')] : []}
      onBlur={onBlur}
      InputProps={{
        readOnly: readonly,
        notched: notched,
        endAdornment,
        startAdornment: (
          <InputAdornment position='start' sx={{ m: 0, pr: 2, py: 0 }}>
            <Stack direction='row' sx={{ width: 70 }}>
              <TextValidator
                name='duration'
                margin='none'
                variant='standard'
                fullWidth
                value={amountOfTimeValue}
                onChange={handleAmountOfTimeChange}
                disabled={unitOfTime === UnitOfTime.Forever || disabled}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    mx: 1,
                  },
                  endAdornment: rateMode && (
                    <InputAdornment position='end' sx={{ m: 0, px: 2, py: 0 }}>
                      <Typography variant='h2' sx={{ flex: 1 }}>
                        {'/'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              {!rateMode && (
                <Divider
                  orientation='vertical'
                  variant={'fullWidth'}
                  flexItem
                  sx={{ my: -0.5 }}
                />
              )}
            </Stack>
          </InputAdornment>
        ),
      }}
      select
    >
      {allowForever && (
        <MenuItem key={UnitOfTime.Forever} value={UnitOfTime.Forever} divider>
          {capitalize(t(`timeUnit.${UnitOfTime.Forever}`, { ns: 'enums' }))}
        </MenuItem>
      )}
      <MenuItem key={UnitOfTime.Minute} value={UnitOfTime.Minute} divider>
        {capitalize(
          t(`timeUnit.${UnitOfTime.Minute}`, {
            ns: 'enums',
            count: plural && !rateMode ? 2 : 1,
          }),
        )}
      </MenuItem>
      <MenuItem key={UnitOfTime.Hour} value={UnitOfTime.Hour} divider>
        {capitalize(
          t(`timeUnit.${UnitOfTime.Hour}`, {
            ns: 'enums',
            count: plural && !rateMode ? 2 : 1,
          }),
        )}
      </MenuItem>
      <MenuItem key={UnitOfTime.Day} value={UnitOfTime.Day} divider>
        {capitalize(
          t(`timeUnit.${UnitOfTime.Day}`, {
            ns: 'enums',
            count: plural && !rateMode ? 2 : 1,
          }),
        )}
      </MenuItem>
      <MenuItem key={UnitOfTime.Week} value={UnitOfTime.Week} divider>
        {capitalize(
          t(`timeUnit.${UnitOfTime.Week}`, {
            ns: 'enums',
            count: plural && !rateMode ? 2 : 1,
          }),
        )}
      </MenuItem>
      <MenuItem key={UnitOfTime.Month} value={UnitOfTime.Month} divider>
        {capitalize(
          t(`timeUnit.${UnitOfTime.Month}`, {
            ns: 'enums',
            count: plural && !rateMode ? 2 : 1,
          }),
        )}
      </MenuItem>
      <MenuItem key={UnitOfTime.Year} value={UnitOfTime.Year} divider>
        {capitalize(
          t(`timeUnit.${UnitOfTime.Year}`, {
            ns: 'enums',
            count: plural && !rateMode ? 2 : 1,
          }),
        )}
      </MenuItem>
    </TextValidator>
  );
};

export default TimeUnitSelect;
